import { Injectable, NgZone, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent, merge, Subscription, of, MonoTypeOperatorFunction } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertMessageService } from './alert-message.service';

@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService implements OnDestroy {
  private isOnlineSubject: BehaviorSubject<boolean>;
  private subscription: Subscription;
  private checkInterval: any;

  /** Observable que emite true=online, false=offline */
  isOnline$: Observable<boolean>;

  /** Getter síncrono do estado atual */
  get isOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    const isBrowser = isPlatformBrowser(this.platformId);
    this.isOnlineSubject = new BehaviorSubject<boolean>(isBrowser ? navigator.onLine : true);
    this.isOnline$ = this.isOnlineSubject.asObservable();

    if (isBrowser) {
      // Escutar eventos de online/offline do browser
      this.subscription = this.ngZone.runOutsideAngular(() => {
        return merge(
          fromEvent(window, 'online').pipe(map(() => true)),
          fromEvent(window, 'offline').pipe(map(() => false)),
        ).subscribe(status => {
          this.ngZone.run(() => {
            if (status) this.setOnline();
            else this.setOffline();
          });
        });
      }) as Subscription;
    }
  }

  /** Forçar atualização do estado (útil para o interceptor) */
  checkStatus(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (navigator.onLine) this.setOnline();
    else this.setOffline();
  }

  /** Marcar como offline manualmente (quando o servidor não responde) */
  setOffline(): void {
    if (!this.isOnlineSubject.value) return; // already offline

    this.isOnlineSubject.next(false);

    // Inicia verificação periódica caso a janela não dispare 'online' (e.g. WiFi ativo, mas sem routeamento de net)
    this.ngZone.runOutsideAngular(() => {
      this.clearCheckInterval();
      this.checkInterval = setInterval(() => {
        this.verifyConnectionWithServer();
      }, 5000); // Tentar novamente de 5 em 5 segundos
    });
  }

  /** Marcar como online manualmente (quando o servidor volta a responder) */
  setOnline(): void {
    if (this.isOnlineSubject.value) return; // already online

    this.isOnlineSubject.next(true);
    this.clearCheckInterval();
  }

  private clearCheckInterval(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Tenta fazer um fetch leve a um recurso local da app
   * Se for bem-sucedido, estamos novamente online!
   */
  private async verifyConnectionWithServer(): Promise<void> {
    try {
      // Usamos fetch nativo para evitar dependências cíclicas com HttpClient + Interceptor
      // E fazemos cache-busting adicionando ?t=timestamp
      const response = await fetch(`/assets/i18n/pt/all.json?t=${new Date().getTime()}`, {
        method: 'HEAD',
        cache: 'no-store',
      });

      if (response.ok) {
        this.ngZone.run(() => {
          this.setOnline();
        });
      }
    } catch (error) {
      // Ignoramos o erro. Significa que continua offline.
    }
  }

  /**
   * Operador RxJS reutilizável para tratar erros de rede.
   * - Se estiver online: mostra alerta de erro e retorna of(null)
   * - Se estiver offline: apenas retorna of(null) sem mostrar alerta
   *
   * @param alerts - Serviço de alertas (opcional, se não fornecido apenas faz log)
   * @param logError - Se deve fazer log do erro na consola (default: true)
   */
  handleNetworkError<T>(alerts?: AlertMessageService, logError = true): MonoTypeOperatorFunction<T> {
    return catchError(error => {
      if (logError) {
        console.log(error);
      }
      // Só mostra o alerta se estiver online (erro real, não de rede)
      if (this.isOnline && alerts) {
        alerts.error('ERRORS.REQUEST_FAILED', 'COMMON.ALERTS.ATTENTION');
      }
      return of(null as T);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearCheckInterval();
  }
}
